import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  CheckCircle,
  Cloud,
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { azureMarketplaceService } from "@/services/azureMarketplace";
import { useToast } from "@/hooks/use-toast";

export function MarketplaceSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const subscriptionId = searchParams.get("subscriptionId");

  useEffect(() => {
    if (!subscriptionId) {
      setError("No subscription ID provided");
      setLoading(false);
      return;
    }

    loadSubscription();
  }, [subscriptionId]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const sub = await azureMarketplaceService.getSubscription();
      if (sub) {
        setSubscription(sub);
      } else {
        setError("Could not load subscription details");
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      setError("Failed to load subscription information");
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate("/app/upload");
  };

  const handleViewBilling = () => {
    navigate("/app/billing");
  };

  if (loading) {
    return (
      <div className="container mx-auto py-16">
        <div className="max-w-2xl mx-auto text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            Loading subscription details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-16">
        <div className="max-w-2xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-4 text-center">
            <Button onClick={() => navigate("/app")} variant="outline">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-16">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            Azure Marketplace Subscription Active!
          </h1>
          <p className="text-muted-foreground">
            Your subscription has been successfully activated
          </p>
        </div>

        {/* What's Next */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What's Next?</CardTitle>
            <CardDescription>
              Get started with Snap Lading AI bill of lading document processing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium">
                1
              </div>
              <div>
                <p className="font-medium">Upload Your First Document</p>
                <p className="text-sm text-muted-foreground">
                  Start by uploading a bill of lading to convert it to EDI
                  format
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium">
                2
              </div>
              <div>
                <p className="font-medium">Process and Download</p>
                <p className="text-sm text-muted-foreground">
                  Our AI will extract data and generate EDI X12 204/211 files
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium">
                3
              </div>
              <div>
                <p className="font-medium">Pay Through Azure</p>
                <p className="text-sm text-muted-foreground">
                  Usage is automatically tracked and billed to your Azure
                  subscription
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Information */}
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Important Information</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>
                Your usage will be billed through your Microsoft Azure
                subscription at the end of each billing cycle
              </li>
              <li>
                You can monitor your usage and manage your subscription in the
                Azure Portal
              </li>
              <li>
                Each document processed counts as one usage unit in your Azure
                billing
              </li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          <Button size="lg" onClick={handleGetStarted}>
            Start Processing Documents
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={handleViewBilling}>
            View Billing Details
          </Button>
        </div>
      </div>
    </div>
  );
}
